import { GROUPS, ITEM_BY_ID, PROXY_GROUP_NAME, type RuleItem } from './catalog.ts';
import { targetOf, type UserConfig } from './config.ts';

export type CustomKind = 'reject' | 'direct' | 'proxy';

export interface CustomEntry {
  id: `custom:${CustomKind}`;
  kind: CustomKind;
  label: string;
  /** 固定去向,写入规则时使用 */
  target: string;
  rank: number;
}

export const CUSTOM_ENTRIES: CustomEntry[] = [
  { id: 'custom:reject', kind: 'reject', label: '自定义拦截', target: 'REJECT', rank: 1 },
  { id: 'custom:direct', kind: 'direct', label: '自定义直连', target: 'DIRECT', rank: 2 },
  { id: 'custom:proxy', kind: 'proxy', label: '自定义代理', target: PROXY_GROUP_NAME, rank: 3 },
];

export const CUSTOM_BY_ID = new Map(CUSTOM_ENTRIES.map((c) => [c.id as string, c]));
export const ORDER_ENTRY_IDS = new Set<string>([...CUSTOM_BY_ID.keys(), ...ITEM_BY_ID.keys()]);

/** 会触发 DNS 解析的兜底规则固定在末尾 */
export const PINNED_TAIL = ['cn-ip'];

export function customDomains(cfg: UserConfig, kind: CustomKind): string[] {
  return kind === 'reject' ? cfg.customReject : kind === 'direct' ? cfg.customDirect : cfg.customProxy;
}

function rank(id: string): number {
  return CUSTOM_BY_ID.get(id)?.rank ?? ITEM_BY_ID.get(id)?.order ?? Number.MAX_SAFE_INTEGER;
}

/** 当前启用的条目(按推荐顺序,不含固定在末尾的) */
function enabledEntries(cfg: UserConfig): string[] {
  const ids = [
    ...CUSTOM_ENTRIES.filter((c) => customDomains(cfg, c.kind).length > 0).map((c) => c.id as string),
    ...cfg.rules.filter((id) => ITEM_BY_ID.has(id) && !PINNED_TAIL.includes(id)),
  ];
  return [...new Set(ids)].sort((a, b) => rank(a) - rank(b));
}

/**
 * 最终的规则顺序:以用户保存的顺序为准;新启用、还没排过序的条目按推荐位置插入;
 * 固定条目追加在末尾
 */
export function effectiveOrder(cfg: UserConfig): string[] {
  const enabled = enabledEntries(cfg);
  const set = new Set(enabled);
  const result = (cfg.order ?? []).filter((id) => set.has(id));
  const placed = new Set(result);
  for (const id of enabled) {
    if (placed.has(id)) continue;
    const at = result.findIndex((e) => rank(e) > rank(id));
    if (at < 0) result.push(id);
    else result.splice(at, 0, id);
    placed.add(id);
  }
  for (const id of PINNED_TAIL) if (cfg.rules.includes(id)) result.push(id);
  return result;
}

export function recommendedOrder(cfg: UserConfig): string[] {
  return effectiveOrder({ ...cfg, order: [] });
}

export function entryLabel(id: string): string {
  return CUSTOM_BY_ID.get(id)?.label ?? ITEM_BY_ID.get(id)?.label ?? id;
}

/** 规则去向的可读描述,只用于比较是否相同与提示文案 */
export function targetLabel(cfg: UserConfig, item: RuleItem): string {
  const t = targetOf(cfg, item);
  if (t === 'own') return `own:${item.id}`;
  if (t === 'cat') return item.group === 'proxy' ? 'proxy' : `cat:${item.group}`;
  return t;
}

function covers(broad: RuleItem, other: RuleItem): boolean {
  if (broad.id === other.id) return false;
  if (broad.covers === 'overseas') return other.group !== 'direct' && other.group !== 'ads' && other.covers !== 'overseas';
  return broad.covers?.includes(other.id) ?? false;
}

export interface Conflict {
  /** 被遮挡的条目 */
  id: string;
  /** 排在上方、会提前匹配的条目 */
  by: string;
  byIndex: number;
}

/** 宽泛规则排在具体规则上方且去向不同时,具体规则的部分流量会被提前匹配 */
export function findConflicts(cfg: UserConfig, order: string[]): Conflict[] {
  const out: Conflict[] = [];
  order.forEach((id, j) => {
    const item = ITEM_BY_ID.get(id);
    if (!item) return;
    for (let i = 0; i < j; i++) {
      const upper = ITEM_BY_ID.get(order[i]);
      if (upper && covers(upper, item) && targetLabel(cfg, upper) !== targetLabel(cfg, item)) {
        out.push({ id, by: upper.id, byIndex: i });
        break;
      }
    }
  });
  return out;
}

export const groupNameOf = (item: RuleItem) => GROUPS.find((g) => g.key === item.group)!.name;
